import Vue from 'vue'
import config from '@/config'
import { getDatesFromPeriod } from './getDatesFromPeriod'
import core from '@altenar/core'
import { IRequestOptions } from '@/interfaces/transport'

const createURLSearchParams = params => {
  return Object.keys(params)
    .map(
      param =>
        encodeURIComponent(param) + '=' + encodeURIComponent(params[param])
    )
    .join('&')
}
/**
 * Base class for data requests and result parsing and processing
 *
 * @category Transport
 */
export class Transport {
  /**
   * Indicator if he Transport loads data first time after the fetch method call
   */
  public loading: boolean = Vue.observable(false)

  /**
   * last parsed response received
   */

  public result: any = Vue.observable(null)

  /**
   * timeout in ms to resend request after an error  (0 = disable resend)
   */

  protected errorDelay?: number

  /**
   * timeout in ms to resend request after success (0 = disable resend)
   */

  protected requestDelay?: number

  /**
   * string represents api origin
   */

  protected origin: string

  /**
   * string represents api method
   */

  protected apiMethod: string

  /**
   * string represents send method POST or GET
   */
  protected requestMethod?: 'POST' | 'GET'

  /**
   * additional headers to send
   */
  protected headers?: object

  /**
   * js timeout object to resend request
   */
  protected timeout?: number

  /**
   * unique id of request, used for indicate if request was stopped
   */
  private fetchId = 0

  /**
   * js fetch object
   */
  private request: any = null

  constructor(options: IRequestOptions) {
    this.origin = options.origin
    this.apiMethod = options.apiMethod
    this.requestMethod = options.requestMethod || 'GET'
    this.errorDelay = options.errorDelay || 1000
    this.requestDelay = options.requestDelay || 0
    this.headers = options.headers || {}
    this.timeout = options.timeout || 0
  }

  /**
   * callback on result received
   * @param result parsed result
   */
  public onAfterReceive(result) {
    return result
  }

  /**
   * callback on error received
   * @param error raw data from server
   */
  public onAfterError(error) {
    return error
  }

  /**
   * received result parser
   * @param response raw data from server
   */
  public parseResult(response) {
    return response
  }

  /**
   * fetch params parser
   * @param params key-value parameters pairs to send
   */
  public parseParams(params) {
    return params
  }

  /**
   * method to stop current request and waiting timeout
   * @param resending indicator used to set loading flag
   */
  public stop(resending?: boolean) {
    this.fetchId = 0
    clearTimeout(this.timeout)
    if (!resending) {
      this.loading = false
    }
  }

  /**
   * method to clear all received data
   */
  public clear() {
    Vue.set(this, 'result', null)
  }

  /**
   *   method to destroy current transport
   */
  public destroy() {
    this.stop()
    this.clear()
  }
  /**
   * method to fetch request with params provided
   * @param params key-value parameters pairs to send
   * @param resending indicator used to set loading flag and resolve options
   */
  public fetch(params?: any, resending: boolean = false) {
    return new Promise((resolve, reject) => {
      this.sendRequest(params, resending, resolve)
    })
  }
  /**
   * method to send low-level request
   * @param params key-value pairs to send as Request body or Query parameters
   * @param resending indicator used to set loading flag and resolve options
   * @param resolvePromise function to call after request was succeeded
   */
  protected sendRequest(
    params: any = {},
    resending?: boolean,
    resolvePromise?: CallableFunction
  ) {
    this.stop(resending)
    // check if js fetch method is ready
    if (!window.fetch) {
      // tslint:disable-next-line:no-console
      console.warn('fetch is undefined, waiting for a polyfill')
      setTimeout(() => {
        this.sendRequest(params, resending, resolvePromise)
      }, 1000)
      return
    }
    // prepare default data to send
    const defaultHeaders: any = {
      // 'Content-Type': 'application/json'
    }
    const defaultBody: any = {
      timezoneOffset: config.timezoneOffset,
      langId: config.langId,
      skinName: config.skinName,
      configId: config.configId,
      numFormat: config.numFormat,
      culture: config.culture,
      countryCode: config.countryCode,
      deviceType: core.config.isMobile ? 'mobile' : 'desktop'
    }
    let requestURL =
      (config.origins[this.origin] || this.origin) + this.apiMethod
    let requestBody = { ...defaultBody }
    // prepare params to send
    const parsedParams = this.parseParams(params)
    for (const prop of Object.keys(parsedParams)) {
      requestBody[prop] = parsedParams[prop]
    }
    getDatesFromPeriod(requestBody)
    // build headers to send
    const headers = this.headers || {}
    const requestHeaders = { ...defaultHeaders }
    for (const prop of Object.keys(headers)) {
      requestHeaders[prop] = headers[prop]
    }
    // build body to send
    let method = this.requestMethod || ''
    if (method.toUpperCase() === 'POST') {
      requestBody = JSON.stringify(requestBody)
    } else {
      method = 'GET'
      requestURL += '?' + createURLSearchParams(requestBody)
      requestBody = null
    }
    // get new fetchId to store
    const fetchId = new Date().getTime()
    this.fetchId = fetchId
    this.loading = !resending || this.loading
    // create and send request
    this.request = fetch(requestURL, {
      method, // *GET, POST, PUT, DELETE, etc.
      mode: 'cors', // no-cors, cors, *same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      headers: requestHeaders,
      body: requestBody
    })
      // response received
      .then(response => {
        this.loading = false
        if (response.status === 200) {
          if (this.fetchId !== fetchId) {
            throw new Error('Fetch resended')
          } else {
            // response is correct
            return response.json()
          }
        } else {
          throw new Error('Network Error')
        }
      })
      .then(response => {
        if (response.Error) {
          throw new Error(response.Error)
        }
        response.Result = response.Result || response
        if (this.requestDelay) {
          // start timeout to resend request
          this.timeout = setTimeout(() => {
            if (this.fetchId === fetchId) {
              this.sendRequest(params, true)
            }
          }, this.requestDelay)
        }
        // process received data
        const parsedResult = this.parseResult(response.Result)
        Vue.set(this, 'result', parsedResult)
        this.onAfterReceive(parsedResult)
        if (!resending && resolvePromise) {
          resolvePromise(parsedResult)
        }
      })
      .catch(error => {
        if (this.fetchId === fetchId) {
          if (this.errorDelay) {
            this.timeout = setTimeout(() => {
              if (this.fetchId === fetchId) {
                this.sendRequest(params, true)
              }
            }, this.errorDelay)
          }
          this.onAfterError(error)
        }
      })
  }
}
